import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { getBaseUrl } from '../utils/apiUtils';
import { AuthContext } from './AuthContext';

export const FriendsContext = createContext();

export const FriendsProvider = ({ children }) => {
  const [friendsList, setFriendsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated } = useContext(AuthContext);

  // Load friends from server or localStorage
  const loadFriends = useCallback(async () => {
    try {
      setLoading(true);
      
      if (isAuthenticated) {
        // User is authenticated, load friends from server
        const response = await fetch(`${getBaseUrl()}/api/user/friends`, {
          credentials: 'include' // Important for sending cookies
        });
        
        const data = await response.json();
        
        if (data.success) {
          setFriendsList(data.friends || []);
          console.log('Friends loaded from server:', data.friends);
        } else {
          console.error('Error loading friends from server:', data.message);
        }
      } else {
        // User is not authenticated, load friends from localStorage
        const storedFriends = localStorage.getItem('friends');
        if (storedFriends) {
          try {
            const parsedFriends = JSON.parse(storedFriends);
            setFriendsList(parsedFriends);
            console.log('Friends loaded from localStorage:', parsedFriends);
          } catch (error) {
            console.error('Error parsing friends from localStorage:', error);
            setFriendsList([]);
          }
        } else {
          setFriendsList([]);
        }
      }
    } catch (error) {
      console.error('Error loading friends:', error);
      setFriendsList([]);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Add friend
  const addFriend = async (username) => {
    if (!username) return;
    
    try {
      if (isAuthenticated) {
        // User is authenticated, add friend to server
        const response = await fetch(`${getBaseUrl()}/api/user/friends`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({ username })
        });
        
        const data = await response.json();
        
        if (data.success) {
          setFriendsList(data.friends);
          console.log('Friend added successfully:', username);
          return true;
        } else {
          console.error('Error adding friend:', data.message);
          return false;
        }
      } else {
        // User is not authenticated, add friend to localStorage
        if (!friendsList.includes(username)) {
          const updatedFriends = [...friendsList, username];
          setFriendsList(updatedFriends);
          localStorage.setItem('friends', JSON.stringify(updatedFriends));
          console.log('Friend added to localStorage:', username);
          return true;
        }
        return false;
      }
    } catch (error) {
      console.error('Error adding friend:', error);
      return false;
    }
  };

  // Remove friend
  const removeFriend = async (username) => {
    if (!username) return;
    
    try {
      if (isAuthenticated) {
        // User is authenticated, remove friend from server
        const response = await fetch(`${getBaseUrl()}/api/user/friends/${encodeURIComponent(username)}`, {
          method: 'DELETE',
          credentials: 'include'
        });
        
        const data = await response.json();
        
        if (data.success) {
          setFriendsList(data.friends);
          console.log('Friend removed successfully:', username);
          return true;
        } else {
          console.error('Error removing friend:', data.message);
          return false;
        }
      } else {
        // User is not authenticated, remove friend from localStorage
        const index = friendsList.indexOf(username);
        if (index !== -1) {
          const updatedFriends = [...friendsList];
          updatedFriends.splice(index, 1);
          setFriendsList(updatedFriends);
          localStorage.setItem('friends', JSON.stringify(updatedFriends));
          console.log('Friend removed from localStorage:', username);
          return true;
        }
        return false;
      }
    } catch (error) {
      console.error('Error removing friend:', error);
      return false;
    }
  };

  // Check if a user is a friend
  const isFriend = (username) => {
    return friendsList.some(friend => friend.toLowerCase() === username.toLowerCase());
  };

  // Load friends when authentication status changes
  useEffect(() => {
    loadFriends();
  }, [isAuthenticated, loadFriends]);

  return (
    <FriendsContext.Provider 
      value={{ 
        friendsList, 
        loading,
        addFriend,
        removeFriend,
        isFriend,
        loadFriends
      }}
    >
      {children}
    </FriendsContext.Provider>
  );
};

export default FriendsProvider;